import { useEffect, useRef, type ReactNode } from 'react'
import { useAppSelector } from '../../../store/hooks.ts'
import type { RootState } from '../../../store'
import css from './Modal.module.css'

interface ModalProps {
    onClose: () => void
    children: ReactNode
    maxWidth?: number
    minHeight?: number
    canClose?: boolean
    /* On mobile, modals are cards with a 24px gap from the screen edges by default.
       Set this for content-heavy modals (e.g. settings) that should stay full-screen. */
    mobileFullScreen?: boolean
}

const Modal = ({ onClose, children, maxWidth = 520, minHeight, canClose = true, mobileFullScreen = false }: ModalProps) => {
    const modalRef = useRef<HTMLDivElement>(null)
    const isConfirmDialogOpen = useAppSelector((state: RootState) => state.ui.confirmDialog.isOpen)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!canClose) return
            if (isConfirmDialogOpen) return

            const isInDropdown = (event.composedPath() as Element[]).some(
                el => el?.getAttribute?.('data-dropdown-portal') === 'true'
            )
            if (isInDropdown) return

            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose()
            }
        }

        const handleEscape = (event: KeyboardEvent) => {
            if (!canClose) return
            if (event.key === 'Escape') {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)

        const scrollEl = (document.querySelector('[data-scroll-container]') as HTMLElement) ?? document.body
        const savedScrollTop = scrollEl.scrollTop
        scrollEl.style.overflow = 'hidden'

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscape)
            scrollEl.style.overflow = ''
            scrollEl.scrollTop = savedScrollTop
        }
    }, [onClose, isConfirmDialogOpen, canClose])

    const overlayClass = mobileFullScreen ? `${css.overlay} ${css.overlay_full}` : css.overlay
    const modalClass = mobileFullScreen ? `${css.modal} ${css.modal_full}` : css.modal

    return (
        <div className={overlayClass}>
            <div
                ref={modalRef}
                className={modalClass}
                style={{ maxWidth: `${maxWidth}px`, ...(minHeight && { minHeight: `${minHeight}px` }) }}
            >
                {children}
            </div>
        </div>
    )
}

export default Modal
